/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { KeyboardUtilsReact } from "abreact/incubator/ui/utils/KeyboardUtilsReact";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbSize, AbSizeContext } from "abreact/misc/AbSize";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import * as AbHooks from "abreact/utils/AbHooks";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import * as React from "react";

/**
 * The properties for the **AbTextArea** component.
 */

/**
 * The **AbTextArea** component allows the user to enter an arbitrary text string.
 */

export const AbTextArea = props => {
  const uniqueId = AbHooks.useUniqueId("ab-text-area");
  const contextSize = React.useContext(AbSizeContext);
  const {
    name,
    value,
    tooltip,
    placeholder,
    resizable,
    rows,
    columns
  } = props;
  const inputProps = ReactUtils.inputProps("ab-text-area", props, ["abui", resizable && "ab-resizable", props.size ?? contextSize ?? AbSize.MEDIUM]);
  const {
    id,
    className,
    style,
    disabled,
    tabIndex,
    autoFocus
  } = inputProps;
  const inputRef = AbHooks.useInputRef(props);

  // Note: there is a Chrome bug where disabled React controls do not fire mouse leave events.
  // To work around this, we don't disable inputs with tooltips explicitly, but instead remove the
  // focus and onClick handling, and visually display the input as disabled.
  //
  // One downside to this approach is that for screen readers and other accessibility tools,
  // the input does not appear to be disabled. This may be something we have to address
  // in the future.
  //
  // See: https://jira.abinitio.com/browse/CAT-1270 and https://github.com/facebook/react/issues/4251
  const useFakeDisabling = disabled && tooltip;
  const onChange = event => {
    props.onChange(event.target.value);
  };
  const onKeyDown = KeyboardUtilsReact.makeKeyHandler([[KeyValue.ENTER, props.onEnter], [KeyValue.ESCAPE, props.onEscape]]);
  return /*#__PURE__*/React.createElement(AbWithTooltip, {
    tooltip: tooltip
  }, /*#__PURE__*/React.createElement("textarea", {
    id: id ?? uniqueId,
    className: className,
    style: style,
    name: name,
    value: value,
    placeholder: disabled ? undefined : placeholder,
    rows: rows,
    cols: columns,
    maxLength: props.maxLength,
    disabled: useFakeDisabling ? false : disabled,
    tabIndex: useFakeDisabling ? -1 : tabIndex,
    onChange: disabled ? undefined : onChange,
    autoFocus: autoFocus,
    onBlur: disabled ? undefined : props.onBlur,
    onFocus: disabled ? undefined : props.onFocus,
    onKeyPress: disabled ? undefined : onKeyDown,
    "data-test-id": props.dataTestId,
    ref: inputRef
  }));
};
//# sourceMappingURL=AbTextArea.js.map
